import { useState, useEffect } from "react";
import { FiClock, FiTool, FiCheckCircle, FiX, FiDollarSign, FiCalendar } from "react-icons/fi";
import fetchData from "../custom_functions/fetchData";
import { url } from "../utils/config";
import { formatDate } from "../helperFunctions/estimateCalculation";

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
});

function formatCurrency(value) {
  const numeric = Number(value);
  return currencyFormatter.format(Number.isFinite(numeric) ? numeric : 0);
}

function VehicleServiceList({ pendingCount = 0, inServiceCount = 0, completedCount = 0 }) {
  const [activeTab, setActiveTab] = useState("pending");
  const [serviceData, setServiceData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);

  // Map tab id to API status parameter
  const statusMap = {
    pending: "Pending",
    inService: "InService",
    completed: "Completed",
  };

  useEffect(() => {
    fetchServiceData(activeTab);
  }, [activeTab]);

  const fetchServiceData = (tab) => {
    setLoading(true);
    const status = statusMap[tab];
    fetchData(
      `${url}/service_requests/${status}`,
      (error) => {
        console.error("Failed to load service data:", error);
        setServiceData([]);
        setLoading(false);
      },
      (response) => {
        setServiceData(response?.data || []);
        setLoading(false);
      },
      false
    );
  };

  const tabs = [
    { id: "pending", label: "Pending", icon: FiClock, color: "accent", count: pendingCount },
    { id: "inService", label: "In Service", icon: FiTool, color: "primary", count: inServiceCount },
    { id: "completed", label: "Completed", icon: FiCheckCircle, color: "secondary", count: completedCount },
  ];

  const getColorClasses = (color) => {
    const colors = {
      accent: "bg-accent-light text-accent border-accent-light",
      primary: "bg-primary-light text-primary border-primary-light",
      secondary: "bg-secondary-light text-secondary border-secondary-light",
    };
    return colors[color];
  };

  const getTabActiveClasses = (color) => {
    const colors = {
      accent: "bg-accent-light border-accent text-accent",
      primary: "bg-primary-light border-primary text-primary",
      secondary: "bg-secondary-light border-secondary text-secondary",
    };
    return colors[color];
  };

  return (
    <div className="bg-white rounded-xl p-6 shadow-base border border-neutral-100">
      <div className="mb-6">
        <h2 className="text-xl font-bold text-neutral-900">Vehicle Service Status - This month</h2>
        <p className="text-sm text-neutral-600 mt-1">Track vehicles across different service stages</p>
      </div>

      {/* Toggle Tabs */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {tabs.map((tab) => {
          const IconComponent = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-semibold text-sm transition-all border-2 ${isActive
                ? getTabActiveClasses(tab.color)
                : "bg-neutral-50 text-neutral-700 border-neutral-200 hover:bg-neutral-100"
                }`}
            >
              <IconComponent size={18} />
              <span>{tab.label}</span>
              <span className={`ml-1 text-xs font-bold px-2 py-0.5 rounded-full ${isActive ? "bg-white" : "bg-neutral-200"}`}>
                {tab.count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Vehicle List */}
      <div className="space-y-3 max-h-[600px] overflow-y-auto">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <p className="text-neutral-500 text-center mt-2">Loading...</p>
          </div>
        ) : serviceData.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <p className="text-neutral-500 text-center">No vehicles in this category</p>
          </div>
        ) : (
          serviceData.map((vehicle, index) => {
            const tabInfo = tabs.find((t) => t.id === activeTab);
            return (
              <div
                key={index}
                className={`border rounded-lg p-4 transition-all hover:shadow-md cursor-pointer ${getColorClasses(tabInfo.color)}`}
                onClick={() => setSelectedVehicle(vehicle)}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-bold text-neutral-900 text-base">
                        {vehicle.year} {vehicle.make} {vehicle.model}
                      </h3>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs text-neutral-600">
                      <div>
                        <span className="font-semibold">VIN:</span> <span className="font-mono">{vehicle.vin}</span>
                      </div>
                      <div>
                        <span className="font-semibold">Request Date:</span> {formatDate(vehicle.requestDate.split("T")[0])}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Service Request Details Modal */}
      {selectedVehicle && selectedVehicle.serviceReq && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-2xl">
            {/* Modal Header */}
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-neutral-100 bg-white px-6 py-4 rounded-t-2xl">
              <div>
                <h3 className="text-lg font-bold text-neutral-900">Service Request Details</h3>
                <p className="text-sm text-neutral-500">
                  {selectedVehicle.year} {selectedVehicle.make} {selectedVehicle.model}
                </p>
              </div>
              <button
                onClick={() => setSelectedVehicle(null)}
                className="rounded-full p-2 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600 transition-colors"
              >
                <FiX size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4">
              {/* Vehicle Info */}
              <div className="rounded-lg border border-neutral-100 bg-neutral-50 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <FiTool size={14} className="text-neutral-600" />
                  <span className="text-xs font-semibold uppercase tracking-wide text-neutral-600">Vehicle Information</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="font-semibold text-neutral-500">VIN:</span> <span className="font-mono">{selectedVehicle.vin}</span></div>
                  <div><span className="font-semibold text-neutral-500">Plate:</span> {selectedVehicle.serviceReq?.fleet_customer_car?.plate || "-"}</div>
                </div>
              </div>

              {/* Request Info */}
              <div className="rounded-lg border border-neutral-100 bg-neutral-50 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <FiCalendar size={14} className="text-neutral-600" />
                  <span className="text-xs font-semibold uppercase tracking-wide text-neutral-600">Request Information</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="font-semibold text-neutral-500">Status:</span> <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${selectedVehicle.serviceReq.status === 'Pending' ? 'bg-amber-100 text-amber-700' : selectedVehicle.serviceReq.status === 'Approved' ? 'bg-emerald-100 text-emerald-700' : selectedVehicle.serviceReq.status === 'Paid' ? 'bg-purple-100 text-purple-700' : 'bg-neutral-100 text-neutral-700'}`}>{selectedVehicle.serviceReq.status}</span></div>
                  <div><span className="font-semibold text-neutral-500">Type:</span> {selectedVehicle.serviceReq.serviceType === 'maintenance' ? 'Maintenance' : 'Service'}</div>
                  <div><span className="font-semibold text-neutral-500">Request Date:</span> {formatDate(selectedVehicle.requestDate?.split("T")[0])}</div>
                  <div><span className="font-semibold text-neutral-500">Payment Term:</span> {selectedVehicle.serviceReq.paymentTerm || "-"}</div>
                </div>
              </div>

              {/* Services List */}
              {Array.isArray(selectedVehicle.serviceReq.serviceList) && selectedVehicle.serviceReq.serviceList.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <FiTool size={14} className="text-emerald-600" />
                    <span className="text-xs font-semibold uppercase tracking-wide text-emerald-600">Services</span>
                  </div>
                  <ul className="space-y-2">
                    {selectedVehicle.serviceReq.serviceList.map((service, index) => (
                      <li
                        key={`service-${index}`}
                        className="flex items-center justify-between rounded-lg border border-emerald-100 bg-emerald-50/50 px-3 py-2 text-xs"
                      >
                        <div>
                          <span className="font-medium text-slate-700">{service?.name || `Service ${index + 1}`}</span>
                          {service?.estimatedTime > 0 && (
                            <span className="ml-2 text-emerald-600">({service.estimatedTime} min)</span>
                          )}
                        </div>
                        <span className="font-semibold text-emerald-700">{formatCurrency(service?.cost ?? 0)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Service Bundles List */}
              {Array.isArray(selectedVehicle.serviceReq.bundleList) && selectedVehicle.serviceReq.bundleList.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <FiTool size={14} className="text-blue-600" />
                    <span className="text-xs font-semibold uppercase tracking-wide text-blue-600">Service Bundles</span>
                  </div>
                  <ul className="space-y-2">
                    {selectedVehicle.serviceReq.bundleList.map((bundle, index) => (
                      <li
                        key={`bundle-${index}`}
                        className="rounded-lg border border-blue-100 bg-blue-50/50 px-3 py-2 text-xs"
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-slate-700">{bundle?.name || `Bundle ${index + 1}`}</span>
                          <span className="font-semibold text-blue-700">{formatCurrency(bundle?.cost ?? 0)}</span>
                        </div>
                        {/* Tire Details - shown if bundle has tire information */}
                        {(bundle?.tireCost !== undefined && bundle?.tireCost !== null) && (
                          <div className="mt-2 pt-2 border-t border-blue-100">
                            <div className="flex items-center gap-1 mb-1">
                              <span className="text-[10px] font-semibold uppercase tracking-wide text-blue-400">Tire Details</span>
                            </div>
                            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
                              {bundle?.tireType && (
                                <div className="flex justify-between">
                                  <span className="text-slate-500">Type:</span>
                                  <span className="font-medium text-slate-700">{bundle.tireType}</span>
                                </div>
                              )}
                              {bundle?.tireQuantity !== undefined && (
                                <div className="flex justify-between">
                                  <span className="text-slate-500">Quantity:</span>
                                  <span className="font-medium text-slate-700">{bundle.tireQuantity}</span>
                                </div>
                              )}
                              <div className="flex justify-between">
                                <span className="text-slate-500">Price Per Tire:</span>
                                <span className="font-medium text-blue-600">{formatCurrency(bundle.tireCost)}</span>
                              </div>
                              {bundle?.tireNotes && (
                                <div className="col-span-2 flex gap-1">
                                  <span className="text-slate-500">Notes:</span>
                                  <span className="font-medium text-slate-700">{bundle.tireNotes}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Parts / Materials List */}
              {Array.isArray(selectedVehicle.serviceReq.materialList) && selectedVehicle.serviceReq.materialList.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <FiTool size={14} className="text-amber-600" />
                    <span className="text-xs font-semibold uppercase tracking-wide text-amber-600">Parts / Materials</span>
                  </div>
                  <ul className="space-y-2">
                    {selectedVehicle.serviceReq.materialList.map((material, index) => (
                      <li
                        key={`material-${index}`}
                        className="flex items-center justify-between rounded-lg border border-amber-100 bg-amber-50/50 px-3 py-2 text-xs"
                      >
                        <div>
                          <span className="font-medium text-slate-700">{material?.name || `Part ${index + 1}`}</span>
                          {material?.estimatedTime > 0 && (
                            <span className="ml-2 text-amber-600">({material.estimatedTime} min)</span>
                          )}
                        </div>
                        <span className="font-semibold text-amber-700">{formatCurrency((material?.cost ?? 0) + (material?.labour ?? 0))}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Custom Services List */}
              {Array.isArray(selectedVehicle.serviceReq.customList) && selectedVehicle.serviceReq.customList.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <FiTool size={14} className="text-purple-600" />
                    <span className="text-xs font-semibold uppercase tracking-wide text-purple-600">Custom Services</span>
                  </div>
                  <ul className="space-y-2">
                    {selectedVehicle.serviceReq.customList.map((custom, index) => (
                      <li
                        key={`custom-${index}`}
                        className="flex items-center justify-between rounded-lg border border-dashed border-purple-200 bg-purple-50/50 px-3 py-2 text-xs"
                      >
                        <span className="font-medium text-slate-700">{custom?.name || custom?.itemName || `Custom Service ${index + 1}`}</span>
                        <span className="font-semibold text-purple-700">{formatCurrency(custom?.cost ?? 0)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Tires List */}
              {Array.isArray(selectedVehicle.serviceReq.tireList) && selectedVehicle.serviceReq.tireList.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <FiTool size={14} className="text-violet-600" />
                    <span className="text-xs font-semibold uppercase tracking-wide text-violet-600">Tires</span>
                  </div>
                  <ul className="space-y-2">
                    {selectedVehicle.serviceReq.tireList.map((tire, index) => (
                      <li
                        key={`tire-${index}`}
                        className="rounded-lg border border-violet-100 bg-violet-50/50 px-3 py-2 text-xs"
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-slate-700">{tire?.name || "Tire"} x {tire?.quantity || 1}</span>
                          <span className="font-semibold text-violet-700">{formatCurrency((tire?.cost ?? 0) * (tire?.quantity || 1))}</span>
                        </div>
                        {tire?.notes && (
                          <div className="mt-1 text-[11px] text-slate-500">
                            <span className="font-semibold">Notes:</span> {tire.notes}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Notes */}
              {selectedVehicle.serviceReq.notes && (
                <div className="rounded-lg border border-neutral-100 bg-neutral-50 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-neutral-600">Notes</span>
                  </div>
                  <p className="text-sm text-neutral-700">{selectedVehicle.serviceReq.notes}</p>
                </div>
              )}

              {/* Cost Summary */}
              <div className="rounded-lg border border-emerald-100 bg-emerald-50 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <FiDollarSign size={14} className="text-emerald-600" />
                  <span className="text-xs font-semibold uppercase tracking-wide text-emerald-600">Cost Summary</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="font-semibold text-neutral-500">Tax ({selectedVehicle.serviceReq.taxRatePercent || 0}%):</span></div>
                  <div className="text-right font-semibold">{formatCurrency(selectedVehicle.serviceReq.tax)}</div>
                  <div className="text-base font-bold text-emerald-700">Total Cost:</div>
                  <div className="text-right text-base font-bold text-emerald-700">{formatCurrency(selectedVehicle.serviceReq.totalCost)}</div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 border-t border-neutral-100 bg-white px-6 py-4 rounded-b-2xl">
              <button
                onClick={() => setSelectedVehicle(null)}
                className="w-full rounded-lg bg-neutral-100 px-4 py-2.5 text-sm font-semibold text-neutral-700 hover:bg-neutral-200 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default VehicleServiceList;
